import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Edit2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const PaymentsDetails = () => {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [newStatus, setNewStatus] = useState("");

  useEffect(() => {
    const fetchPayment = async () => {
      try {
        const response = await axios.get(
          `${API}/admin/payments/${paymentId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        // Find the specific payment by ID
        const foundPayment = response.data;
        if (foundPayment) {
          setPayment(foundPayment);
          setNewStatus(foundPayment.status);
        } else {
          toast.error("Payment not found");
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to load payment details");
      } finally {
        setLoading(false);
      }
    };

    fetchPayment();
  }, [paymentId]);

  const handleUpdateStatus = async () => {
    if (newStatus === payment.status) {
      setIsEditing(false);
      return;
    }

    try {
      await axios.put(
        `${API}/admin/payments/${paymentId}?status=${newStatus}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setPayment({ ...payment, status: newStatus });
      setIsEditing(false);
      toast.success("Payment status updated successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update payment status");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-6 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading invoice...</div>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="max-w-4xl mx-auto bg-white p-8 rounded-lg shadow">
          <p className="text-center text-gray-600">Invoice not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header Actions */}
        <div className="mb-6 flex items-center justify-between">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-[#1A3C34] hover:bg-[#F5F2EB]"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back
          </Button>
          <div className="flex gap-3">
            <Button
              onClick={handlePrint}
              className="bg-[#1A3C34] hover:bg-[#0f2622] text-white"
            >
              <Download className="mr-2" size={20} />
              Download / Print
            </Button>
          </div>
        </div>

        {/* Invoice Container */}
        <div className="bg-white rounded-lg shadow-lg p-12 print:shadow-none">
          {/* Invoice Header */}
          <div className="mb-8 pb-8 border-b-2 border-gray-200">
            <div className="flex justify-between items-start mb-8">
              <div>
                <h1 className="text-4xl font-bold text-[#1A3C34] mb-2">INVOICE</h1>
                <p className="text-gray-600">{payment.invoice_id}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">ParkSmart</p>
                <p className="text-sm text-gray-600">Parking Management System</p>
              </div>
            </div>
          </div>

          {/* Invoice Details Grid */}
          <div className="grid grid-cols-2 gap-8 mb-12">
            {/* Bill To */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-4 uppercase">
                Bill To
              </h3>
              <p className="text-lg font-semibold text-gray-900 mb-1">
                {payment.organisation_name}
              </p>
              <p className="text-sm text-gray-600">Organization Account</p>
            </div>

            {/* Invoice Info */}
            <div className="text-right">
              <div className="mb-4">
                <p className="text-xs text-gray-600 uppercase">Invoice Date</p>
                <p className="text-lg font-semibold text-gray-900">
                  {new Date(payment.created_at).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600 uppercase">Invoice Number</p>
                <p className="text-lg font-semibold text-gray-900">
                  {payment.invoice_id}
                </p>
              </div>
            </div>
          </div>

          {/* Invoice Period */}
          <div className="grid grid-cols-2 gap-8 mb-12 p-6 bg-gray-50 rounded-lg">
            <div>
              <p className="text-xs text-gray-600 uppercase font-semibold mb-2">
                Invoice Period Start
              </p>
              <p className="text-lg text-gray-900">
                {new Date(payment.invoice_start_date).toLocaleDateString()}
              </p>
            </div>
            <div style={{textAlign:'end'}}>
              <p className="text-xs text-gray-600 uppercase font-semibold mb-2">
                Invoice Period End
              </p>
              <p className="text-lg text-gray-900">
                {new Date(payment.invoice_end_date).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Amount Section */}
          <div className="mb-8 p-6 bg-[#1A3C34] text-white rounded-lg">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm opacity-90 mb-2">Total Amount Due</p>
                <p className="text-4xl font-bold">
                  ₹{parseFloat(payment.amount).toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm opacity-90 mb-2">Payment Status</p>
                {isEditing ? (
                  <div className="flex gap-2">
                    <select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="px-3 py-2 bg-white text-[#1A3C34] rounded text-sm"
                    >
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="overdue">Overdue</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    <Button
                      onClick={handleUpdateStatus}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Save
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 justify-end">
                    <span
                      className={`px-4 py-2 rounded-full text-sm font-semibold ${
                        payment.status === "paid"
                          ? "bg-green-100 text-green-800"
                          : payment.status === "pending"
                          ? "bg-yellow-100 text-yellow-800"
                          : payment.status === "overdue"
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {payment.status.charAt(0).toUpperCase() +
                        payment.status.slice(1)}
                    </span>
                    <Button
                      onClick={() => setIsEditing(true)}
                      size="sm"
                      variant="ghost"
                      className="text-white hover:bg-white/20"
                    >
                      <Edit2 size={16} />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-xs text-gray-600 text-center">
              This is an automated invoice. For inquiries, please contact ParkSmart support.
            </p>
            <p className="text-xs text-gray-600 text-center mt-2">
              © 2026 ParkSmart. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            background: white;
          }
          .print\\:shadow-none {
            box-shadow: none;
          }
        }
      `}</style>
    </div>
  );
};

export default PaymentsDetails;
